import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { LANGUAGES } from '@/config/languages'
import { cn } from '@/lib/utils'

/**
 * The EN | ລາວ toggle from the design.
 *
 * i18next persists the choice to localStorage and the font preloader reacts to
 * `languageChanged`, so switching also updates `<html lang>` and warms the Lao
 * faces — nothing else needs wiring here.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { t, i18n } = useTranslation()
  const active = i18n.resolvedLanguage ?? i18n.language

  return (
    <div
      className={cn('flex items-center', className)}
      role="group"
      aria-label={t('language.label')}
    >
      {LANGUAGES.map((language, index) => {
        const isActive = language.code === active
        return (
          <Fragment key={language.code}>
            {index > 0 && <span aria-hidden className="bg-on-primary/25 h-4 w-px" />}
            <button
              type="button"
              lang={language.code}
              aria-current={isActive ? 'true' : undefined}
              aria-label={t('language.switchTo', { language: language.label })}
              onClick={() => void i18n.changeLanguage(language.code)}
              className={cn(
                'text-label-md rounded-sm px-3 py-1.5 transition-colors duration-200',
                isActive
                  ? 'text-on-primary font-semibold'
                  : 'text-on-primary/70 hover:bg-on-primary/10 hover:text-on-primary',
              )}
            >
              {language.label}
            </button>
          </Fragment>
        )
      })}
    </div>
  )
}
